// CustomerListTableWithDialog.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const OtherCheckList = () => {
  const getSubsidyLabel = (row, value) => {
    if (row?.solarType == 'NDCR') return 'Not Applicable';
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Not Applicable';
  };
  const getSubsidyValue = (value) => {
    if (value === true) return true;
    if (value === false) return false;
    if (value === null) return true;
    return null;
  };

  const getSubsidyColor = (value) => {
    if (value === true) return 'success';
    if (value === false) return 'error';
    return 'default'; // or warning / info
  };
  const columns = useMemo(
    () => [
      // {
      //   field: 'view',
      //   headerName: 'View',
      //   sortable: false,
      //   minWidth: 80,
      //   renderCell: () => (
      //     <Link variant="body2" sx={{ fontWeight: 400 }} href="/customer-details">
      //       <RemoveRedEyeOutlinedIcon />
      //     </Link>
      //   ),
      // },
      {
        field: 'client',
        headerName: 'Client',
        minWidth: 280,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          const { firstName, lastName, mobile } = params.row;
          return (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                alt={firstName.toUpperCase()}
                src={firstName}
                sx={{ width: 24, height: 24, bgcolor: '#977efdff' }}
              />
              <Box>
                <Typography variant="subtitle2">{`${firstName} ${lastName}`}</Typography>
                <Link href="#!" variant="caption">
                  {mobile || ''}
                </Link>
              </Box>
            </Stack>
          );
        },
      },

      {
        field: 'subsidyFiled',
        headerName: 'Subsidy Filed',
        minWidth: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          const value = params?.row?.othermanagements?.subsidyFiled;
          return (
            <Stack>
              <Chip
                label={getSubsidyLabel(params.row, value)}
                variant="soft"
                color={getSubsidyColor(value)}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          );
        },
      },
      {
        field: 'subsidyreceived',
        headerName: 'Subsidy Received',
        minWidth: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => {
          const value = params?.row?.othermanagements?.subsidyReceived;
          return (
            <Stack>
              <Chip
                label={getSubsidyLabel(params.row, value)}
                variant="soft"
                color={getSubsidyColor(value)}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          );
        },
      },
      {
        field: 'fileHandOver',
        headerName: 'Is File Hand Over?',
        minWidth: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => (
          <Stack>
            <Chip
              label={params?.row?.othermanagements?.fileHandover ? 'yes' : 'no'}
              variant="soft"
              color={getStatusBadgeColor(
                params?.row?.othermanagements?.fileHandover ? 'yes' : 'no',
              )}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
        ),
      },
      {
        field: 'siteReview',
        headerName: 'Site Review',
        minWidth: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => (
          <Stack>
            <Chip
              label={params?.row?.othermanagements?.siteReview ? 'yes' : 'no'}
              variant="soft"
              color={getStatusBadgeColor(params?.row?.othermanagements?.siteReview ? 'yes' : 'no')}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
        ),
      },
      {
        field: 'installation',
        headerName: 'Installation CheckList',
        width: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => (
          <Button variant="outlined" size="small" onClick={() => handleOpenDialog(params.row)}>
            Update
          </Button>
        ),
      },
      // {
      //   field: 'action',
      //   headerName: 'Action',
      //   sortable: false,
      //   align: 'right',
      //   width: 60,
      //   headerAlign: 'right',
      //   renderCell: () => <DashboardMenu />,
      // },
    ],
    [],
  );

  const [data, setdata] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({
    subsidyFiled: false,
    subsidyReceived: false,
    fileHandover: false,
    siteReview: false,
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
    search: '',
  });
  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search }));
    }, 700);
    return () => clearTimeout(timeOut);
  }, [search]);

  useEffect(() => {
    if (selectedRow) {
      setFormData({
        subsidyFiled: getSubsidyValue(selectedRow.othermanagements.subsidyFiled) || false,
        subsidyReceived: getSubsidyValue(selectedRow.othermanagements.subsidyReceived) || false,
        fileHandover: selectedRow.othermanagements.fileHandover || false,
        siteReview: selectedRow.othermanagements.siteReview || false,
      });
    }
  }, [selectedRow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev };

      updated[name] = value === 'true';
      if (name === 'subsidyFiled' && value !== 'true') {
        updated.subsidyReceived = false;
        updated.fileHandover = false;
        updated.siteReview = false;
      } else if (name === 'subsidyReceived' && value !== 'true') {
        updated.fileHandover = false;
        updated.siteReview = false;
      } else if (name === 'fileHandover' && !value) {
        updated.siteReview = false;
      }
      return updated;
    });
  };
  const handleNDCRChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev };

      updated[name] = value === 'true';

      // reset dependent fields if step breaks
      if (name === 'subsidyFiled' && value !== 'true') {
        updated.subsidyReceived = false;
        updated.fileHandover = false;
        updated.siteReview = false;
      } else if (name === 'subsidyReceived' && value !== 'true') {
        updated.fileHandover = false;
        updated.siteReview = false;
      } else if (name === 'fileHandover' && value !== 'true') {
        updated.siteReview = false;
      }

      return updated;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // /list_otherManagement
      const response = await post('users/list_customers_stages', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        type: 'other',
      });
      if (response.status == 200) {
        setdata(response.data.data);
        setTotal(response.data.total);
      } else {
        enqueueSnackbar(response.message || 'Failed to update GEDA details', {
          variant: 'error',
          autoHideDuration: 3000,
        });
        setdata([]);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to update GEDA details', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      setdata([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  const handleOpenDialog = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setFormData({
      subsidyFiled: false,
      subsidyReceived: false,
      fileHandover: false,
      siteReview: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        subsidyFiled: formData.subsidyFiled,
        subsidyReceived: formData.subsidyReceived,
        fileHandover: formData.fileHandover,
        siteReview: formData.siteReview,
      };

      const response = await post(
        `users/update_otherManagement/${selectedRow.othermanagements._id}`,
        payload,
      );

      if (response.status === 200) {
        enqueueSnackbar('Other Management details updated successfully', { variant: 'success' });
        handleCloseDialog();
        fetchData(); // refresh table
      } else {
        enqueueSnackbar(response.message || 'Failed to update Other Management details', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
    }
  };

  const getStatusBadgeColor = (val) => {
    switch (val) {
      case 'yes':
        return 'success';
      case 'hold':
        return 'info';
      case 'reject':
        return 'error';
      case 'no':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <StyledTextField
          id="search-box"
          type="search"
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Customer Name"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: { sm: 200, md: 240 },
            flexGrow: { xs: 1, sm: 0 },
            mb: 3,
          }}
        />
        <DataGrid
          loading={loading}
          rowHeight={72}
          rows={data}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel} // { page: 0, pageSize: 10 }
          onPaginationModelChange={(e) =>
            setPaginationModel({ page: e.page, pageSize: e.pageSize, search })
          }
          pageSizeOptions={[5, 10, 20]}
          sx={{
            '& .margin': {
              pr: 3,
            },
            '& .MuiDataGrid-columnHeaders': {
              minWidth: '100%',
            },
          }}
          slots={{
            basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
          }}
        />
      </Box>
      {/* DIALOG FORM */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Other Details – {selectedRow?.firstName} {selectedRow?.lastName}
        </DialogTitle>
        {selectedRow?.solarType == 'DCR' ? (
          <DialogContent>
            <Box component="form" id="gedaForm" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {/* a. Approved by GEDA */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                a. Is Subsidy Filed ?
              </Typography>
              <RadioGroup
                row
                name="subsidyFiled"
                value={formData.subsidyFiled}
                onChange={handleChange}
              >
                <FormControlLabel value={true} control={<Radio />} label="Yes" />
                <FormControlLabel value={false} control={<Radio />} label="No" />
              </RadioGroup>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                b. Is Subsidy Received ?
              </Typography>
              <RadioGroup
                row
                name="subsidyReceived"
                value={formData.subsidyReceived}
                onChange={handleChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={!formData.subsidyFiled}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={!formData.subsidyFiled}
                />
              </RadioGroup>
              {/* a. Document uploaded */}
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                c. Is File Hand Over?
              </Typography>
              <RadioGroup
                row
                name="fileHandover"
                value={formData.fileHandover}
                onChange={handleChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={!formData.subsidyReceived}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={!formData.subsidyReceived}
                />
              </RadioGroup>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                d. Is Site Review Taken ?
              </Typography>
              <RadioGroup row name="siteReview" value={formData.siteReview} onChange={handleChange}>
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={!formData.fileHandover}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={!formData.fileHandover}
                />
              </RadioGroup>

              {/* d. Meter charges */}
            </Box>
          </DialogContent>
        ) : (
          <DialogContent>
            <Box component="form" id="gedaForm" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {/* a. Approved by GEDA */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                a. Is Subsidy Filed ?
              </Typography>
              <RadioGroup row name="subsidyFiled" value={true} onChange={handleNDCRChange}>
                <FormControlLabel value={true} control={<Radio />} label="Not Applicable" />
              </RadioGroup>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                b. Is Subsidy Received ?
              </Typography>
              <RadioGroup row name="subsidyReceived" value={true} onChange={handleNDCRChange}>
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Not Applicable"
                  disabled={!formData.subsidyFiled}
                />
              </RadioGroup>
              {/* a. Document uploaded */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                c. Is File Hand Over?
              </Typography>
              <RadioGroup
                row
                name="fileHandover"
                value={formData.fileHandover}
                onChange={handleNDCRChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={!formData.subsidyReceived}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={!formData.subsidyReceived}
                />
              </RadioGroup>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                d. Is Site Review Taken ?
              </Typography>
              <RadioGroup
                row
                name="siteReview"
                value={formData.siteReview}
                onChange={handleNDCRChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={!formData.fileHandover}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={!formData.fileHandover}
                />
              </RadioGroup>

              {/* d. Meter charges */}
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button type="submit" form="gedaForm" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OtherCheckList;
